import React, { useRef, useState } from 'react';
import { GetServerSideProps } from 'next';
import { ServerResponse } from 'http';
import type { WRelatedLinkView, WeddingSearchHit } from '@zola/svc-web-api-ts-client';
import { UserContext } from '@zola-helpers/client/dist/es/@types';
import { CategoryTiles, SupportAccordion } from '@zola/zola-ui/src/components/PreAuthV3';

import CoupleSearchResultsSection from 'components/CoupleSearch/CoupleSearchResultsSection';
import {
  FindACouplePageWrapper,
  SearchWrapper,
} from 'components/LP/v3/FindACouplePageWrapper.styles';
import CoupleSearch from 'components/CoupleSearch/v3/components/CoupleSearch';
import { reviewItems } from 'components/CoupleSearch/v3/content/reviews';
import FindACoupleMeta from 'components/CoupleSearch/meta';

import { fetchNavHTML } from 'libs/server/nav';
import Nav from 'components/Nav';
import { fetchUser } from 'libs/server/users';
import IncomingMessageWithCookies from 'types/IncomingMessageWithCookies';

import staticFlags, { FIND_A_COUPLE_V3_VISIBLE } from 'libs/universal/utils/staticFlags';
import { articleTiles } from 'components/CoupleSearch/v3/content/articleTiles';
import Values from '@zola/zola-ui/src/components/PreAuthV3/Values';
import { COLORS3 } from '@zola/zola-ui/src/styles/emotion';
import { useIsPostAuthNav3MobileOrDesktop } from 'hooks/useIsPostAuthNav3Active';
import { fetchSEORelatedLinks } from 'libs/server/seo';
import Footer from 'components/ui/Footer/Footer';

interface CoupleSearchProps {
  navHTML: string | null;
  userCtx: UserContext | null;
  adviceLinks: WRelatedLinkView[];
}

const CoupleSearchV3: React.FC<CoupleSearchProps> = (props): JSX.Element => {
  const { navHTML, userCtx, adviceLinks } = props;
  const [result, setResult] = useState<Array<WeddingSearchHit> | undefined>();
  const searchResultsRef = useRef<HTMLDivElement>(null);

  const isPostAuthNav3Enabled = useIsPostAuthNav3MobileOrDesktop(userCtx);

  return (
    <>
      <FindACoupleMeta />
      <Nav navHTML={navHTML} showPromo user={null} disablePrimaryNavCollapse disableSecondary />
      <FindACouplePageWrapper isPostAuthNav3={isPostAuthNav3Enabled}>
        <SearchWrapper>
          <CoupleSearch handleSearchResult={setResult} searchResultsRef={searchResultsRef} />
          {result && (
            <CoupleSearchResultsSection
              couplesSearchResult={result}
              searchResultsRef={searchResultsRef}
            />
          )}
        </SearchWrapper>
        <Values
          valueOne={
            <>
              Loved by over <span> 2 million couples</span>
            </>
          }
          valueTwo={
            <>
              Free shipping, price matching, <span> and more</span>
            </>
          }
          valueThree={
            <>
              And world class<span> customer service</span>
            </>
          }
          backgroundColor={COLORS3.COVE_100}
          fontColor={COLORS3.CLOUD_100}
        />
        <CategoryTiles
          title="Wedding guest 101 (You’ll thank us later)"
          categories={articleTiles}
          square
          cta={{
            linkText: 'View all',
            href: '/expert-advice/topic/wedding-guests',
          }}
        />
        <div>
          <SupportAccordion advice={{ links: adviceLinks }} reviews={reviewItems} />
        </div>
        <Footer userCtx={userCtx} />
      </FindACouplePageWrapper>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<CoupleSearchProps> = async ({
  req,
  res,
}: {
  req: IncomingMessageWithCookies;
  res: ServerResponse;
}) => {
  const sendRedirectLocation = (location: string) => {
    res.writeHead(302, {
      Location: location,
    });
    res.end();
    return { props: {} }; // stop execution
  };

  if (!staticFlags.get(FIND_A_COUPLE_V3_VISIBLE)) {
    sendRedirectLocation('/search/wedding-registry');
  }
  const [navHTML, userCtx, adviceLinks] = await Promise.all([
    fetchNavHTML(req),
    fetchUser(req),
    fetchSEORelatedLinks({
      topicSlug: 'registry-gifts',
      subtopicSlug: 'wedding-registry-basics',
    }),
  ]);

  return {
    props: {
      navHTML,
      userCtx,

      adviceLinks,
    },
  };
};

export default CoupleSearchV3;
